import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, message, Typography, Popconfirm, Radio, Modal, Input, Space } from 'antd';
import dayjs from 'dayjs';
import { storeRedemptionApi } from '../../services/api';

const { Title } = Typography;

// 订单状态：0-备货中 1-待领取 2-已领取 3-已取消
const STATUS = {
  0: { label: '备货中', color: 'orange' },
  1: { label: '待领取', color: 'gold' },
  2: { label: '已领取', color: 'green' },
  3: { label: '已取消', color: 'default' },
};

const StoreRedemptions = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');

  // 取货弹窗
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupOrder, setPickupOrder] = useState(null);
  const [pickupCode, setPickupCode] = useState('');
  const [pickupLoading, setPickupLoading] = useState(false);

  useEffect(() => {
    fetchData(1);
  }, [statusFilter]);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const res = await storeRedemptionApi.list({ page, limit: 10, status: statusFilter });
      setData(res.data.list);
      setPagination({ ...pagination, current: page, total: res.data.pagination.total });
    } catch (err) {
      if (!err.response) message.error('网络错误，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleArrive = async (id) => {
    try {
      const res = await storeRedemptionApi.arrive(id);
      message.success(res.message || '已标记到货');
      fetchData(pagination.current);
    } catch (err) {
      if (!err.response) message.error('网络错误，请重试');
    }
  };

  const handleCancel = async (id) => {
    try {
      const res = await storeRedemptionApi.cancel(id);
      message.success(res.message || '已取消');
      fetchData(pagination.current);
    } catch (err) {
      if (!err.response) message.error('网络错误，请重试');
    }
  };

  const openPickup = (record) => {
    setPickupOrder(record);
    setPickupCode('');
    setPickupOpen(true);
  };

  const confirmPickup = async () => {
    if (!pickupCode.trim()) {
      message.warning('请输入客人提供的取货码');
      return;
    }
    setPickupLoading(true);
    try {
      const res = await storeRedemptionApi.pickup(pickupOrder.id, pickupCode.trim());
      message.success(res.message || '取货成功');
      setPickupOpen(false);
      fetchData(pagination.current);
    } catch (err) {
      if (!err.response) message.error('网络错误，请重试');
    } finally {
      setPickupLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '会员', dataIndex: 'nickname', width: 100, render: (v, r) => v || r.member_no || '-' },
    { title: '手机号', dataIndex: 'phone', width: 115, render: (v) => v || '-' },
    { title: '礼品', dataIndex: 'product_name' },
    { title: '积分', dataIndex: 'points_cost', width: 80, render: (v) => <span style={{ color: '#A98E4E', fontWeight: 600 }}>{v}</span> },
    { title: '领取码', dataIndex: 'pickup_code', width: 110, render: (v) => <code>{v}</code> },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (v) => <Tag color={STATUS[v]?.color}>{STATUS[v]?.label || v}</Tag>
    },
    {
      title: '兑换时间', dataIndex: 'created_at', width: 150,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作', width: 200, render: (_, r) => (
        <Space>
          {r.status === 0 && (
            <Button type="link" size="small" style={{ color: '#A98E4E' }} onClick={() => handleArrive(r.id)}>
              到货
            </Button>
          )}
          {r.status === 1 && (
            <Button type="link" size="small" style={{ color: '#A98E4E' }} onClick={() => openPickup(r)}>
              客人取货
            </Button>
          )}
          {(r.status === 0 || r.status === 1) && (
            <Popconfirm
              title="确认取消兑换？"
              description={`将退回 ${r.points_cost} 积分给会员`}
              okText="确认取消"
              cancelText="再想想"
              onConfirm={() => handleCancel(r.id)}
            >
              <Button type="link" size="small" danger>取消兑换</Button>
            </Popconfirm>
          )}
        </Space>
      )
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>兑换订单</Title>
        <Radio.Group value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <Radio.Button value="">全部</Radio.Button>
          <Radio.Button value="0">备货中</Radio.Button>
          <Radio.Button value="1">待领取</Radio.Button>
          <Radio.Button value="2">已领取</Radio.Button>
          <Radio.Button value="3">已取消</Radio.Button>
        </Radio.Group>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{ ...pagination, showSizeChanger: false, showTotal: (t) => `共 ${t} 单` }}
        onChange={(p) => fetchData(p.current)}
      />

      {/* 客人取货：验证取货码 */}
      <Modal
        title="客人取货 — 验证取货码"
        open={pickupOpen}
        onCancel={() => setPickupOpen(false)}
        onOk={confirmPickup}
        confirmLoading={pickupLoading}
        okText="确认取货"
        cancelText="取消"
        okButtonProps={{ style: { background: '#A98E4E' } }}
      >
        <div style={{ marginBottom: 16, color: '#666' }}>
          礼品：<strong>{pickupOrder?.product_name}</strong>（{pickupOrder?.points_cost} 积分）<br />
          会员：{pickupOrder?.nickname || pickupOrder?.member_no}
        </div>
        <Input
          size="large"
          placeholder="请输入客人出示的取货码"
          value={pickupCode}
          onChange={(e) => setPickupCode(e.target.value)}
          onPressEnter={confirmPickup}
          style={{ letterSpacing: 2, textAlign: 'center' }}
          maxLength={20}
        />
      </Modal>
    </div>
  );
};

export default StoreRedemptions;
